import { zodResolver } from "@hookform/resolvers/zod";
import { CountryCode } from "libphonenumber-js";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert, Button, DropdownSelect, ErrorMessage, Modal, PhoneNumberInput, TextInput, Typography } from "@/components/atoms";
import { TITLE_OPTIONS, passengerContactSchema } from "@/components/organisms/MyFavourites/GuestPassengersTable/guestForm";
import { PassengerContact } from "@/redux/slices/clientBooking/types";
import { normalisePhone } from "@/utils";
import { useOperatorPhoneCountry } from "./useOperatorPhoneCountry";

const editPassengerSchema = passengerContactSchema.extend({ title: z.string() });
type PassengerContactForm = z.infer<typeof editPassengerSchema>;

const toForm = (passenger: PassengerContact, country: CountryCode | undefined): PassengerContactForm => ({
  title: passenger.title ?? "",
  firstName: passenger.firstName,
  lastName: passenger.lastName,
  email: passenger.email,
  contactNumber: normalisePhone(passenger.phone ?? "", country),
});

interface EditPassengerDetailsModalProps {
  passenger: PassengerContact | null;
  onClose: () => void;
  onSave: (details: PassengerContact) => void;
}

export const EditPassengerDetailsModal = ({ passenger, onClose, onSave }: EditPassengerDetailsModalProps) => {
  const country = useOperatorPhoneCountry();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PassengerContactForm>({ resolver: zodResolver(editPassengerSchema) });

  useEffect(() => {
    if (passenger) reset(toForm(passenger, country));
  }, [passenger, country, reset]);

  const onSubmit = (form: PassengerContactForm) =>
    passenger &&
    onSave({
      title: form.title,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email.trim(),
      phone: form.contactNumber === normalisePhone(passenger.phone, country) ? passenger.phone : form.contactNumber,
    });

  return (
    <Modal
      open={!!passenger}
      onClose={onClose}
      variant="web-small"
      title="Edit Passenger Details"
      description="Update this passenger’s details for this booking."
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit(onSubmit)}>
            Save Changes
          </Button>
        </>
      )}
    >
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <Alert
          type="info"
          className="border border-info"
          message={
            <>
              <span className="block">Changes apply to this booking only.</span>
              <span className="block font-normal">
                To update this passenger for future bookings, edit them in My Favourites › Guest Passengers, or in My Profile if it’s you.
              </span>
            </>
          }
        />

        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Title
          </Typography>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                size="md"
                hasError={!!errors.title}
                placeholder="Select Title"
                options={TITLE_OPTIONS}
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
          <ErrorMessage errors={errors} name="title" />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="edit-passenger-first-name">
            <Typography variant="action" className="text-neutral-dark-gray">
              First Name
            </Typography>
          </label>
          <TextInput
            id="edit-passenger-first-name"
            size="md"
            placeholder="Enter First Name"
            hasError={!!errors.firstName}
            {...register("firstName")}
          />
          <ErrorMessage errors={errors} name="firstName" />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="edit-passenger-last-name">
            <Typography variant="action" className="text-neutral-dark-gray">
              Last Name
            </Typography>
          </label>
          <TextInput
            id="edit-passenger-last-name"
            size="md"
            placeholder="Enter Last Name"
            hasError={!!errors.lastName}
            {...register("lastName")}
          />
          <ErrorMessage errors={errors} name="lastName" />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="edit-passenger-email">
            <Typography variant="action" className="text-neutral-dark-gray">
              Email
            </Typography>
          </label>
          <TextInput
            id="edit-passenger-email"
            type="email"
            size="md"
            placeholder="Enter Email Address"
            hasError={!!errors.email}
            {...register("email")}
          />
          <ErrorMessage errors={errors} name="email" />
        </div>

        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Contact Number #1
          </Typography>
          <PhoneNumberInput control={control} name="contactNumber" size="md" hasError={!!errors.contactNumber} />
          <ErrorMessage errors={errors} name="contactNumber" />
        </div>
      </form>
    </Modal>
  );
};
